import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Group, IValueViewModel, ISortConfiguration, Dictionary, SeriesSelectorViewModel, ObjectType } from '../../../models';
import { FlowChartSeries } from '../../../widgets';
import { ISeriesService, ISeriesSelectorService } from '../../../services';
import { groupIdentifierSeparator, infoTextColor, infoBorderColor, whiteBackgroundColor } from '../../../common/constants';
import { seriesAdded, wtDroppableDrop } from '../../../common/events';
import { toDictionary } from '../../../common/helpers';

interface FlowChartSerieRow {
    viewModel: IValueViewModel;
    series: FlowChartSeries;
    name: string;
    isBindable: boolean;
}

class FlowChartSeriesController implements angular.IComponentController {
    groups: Group[];
    groupSeparator = groupIdentifierSeparator;
    isBindable: boolean;
    isLoading: boolean;
    loadingMessage: string;
    rows: FlowChartSerieRow[];
    selectedItem: FlowChartSeries;
    series: FlowChartSeries[];
    sortConfig: ISortConfiguration;
    viewModels: IValueViewModel[];

    static $inject = [
        '$scope',
        'seriesService',
        'seriesSelectorService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _seriesService: ISeriesService,
        private _seriesSelectorService: ISeriesSelectorService
    ) {
        this.sortConfig = {
            animation: 150,
            onSort: undefined,
            handle: '.drag-handle'
        };
    }

    $onInit() {
        this._$scope.$on(seriesAdded, (event, data) => this.add(data));

        this._$scope.$on(wtDroppableDrop, (event, data) => {
            if (data && data.itemId) {
                if (data.binId) {
                    const bin = data.binId.split('+');
                    this.update(bin[0], bin[1], data.itemId);
                } else {
                    this.addByGuid(data.itemId);
                }
            }
        });

        if (!this.series) {
            this.series = [];
        }
        this.updateRows(false);
        this.resetSelection();
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        this.updateRows(false);
    }

    private updateRows(bind: boolean = false) {
        if (this.series && this.viewModels) {
            this.isLoading = true;
            this.mapRows(toDictionary(this.viewModels), bind);
            this.isLoading = false;
        }
    }

    private mapRows(viewModels: Dictionary<IValueViewModel>, bind: boolean) {
        this.rows = this.series.map(d => {
            const vm = viewModels[d.guid];
            if (bind && vm) {
                d.name = vm.name;
                this._seriesService.bind(d, vm.groupAbsolutePath);
            }
            return {
                viewModel: vm ?? null,
                series: d,
                name: vm
                    ? (vm.groupUniqueName ? vm.groupUniqueName + groupIdentifierSeparator + vm.name : vm.name)
                    : '',
                isBindable: vm.objectType === ObjectType.Identifier
            };
        });
    }

    remove(serie) {
        _.remove(this.series, s => s.guid === serie.guid);
        this.updateRows();
    }

    select(serie: FlowChartSeries) {
        this.resetSelection();
        serie.selected = true;
        this.selectedItem = serie;
    }

    private resetSelection() {
        this.series.forEach(s => s.selected = false);
    }

    private add(serie: SeriesSelectorViewModel) {
        this.addByGuid(serie.guid);
    }

    private addByGuid(guid: string) {
        const newSeries: FlowChartSeries = {
            guid: guid,
            value: 'average',
            isBoundToGroup: false,
            backgroundColor: whiteBackgroundColor,
            borderColor: infoBorderColor,
            showGroup: false,
            showQuality: false,
            showUnit: true,
            showValue: true,
            textColor: infoTextColor,
            position: {
                x: 0,
                y: 0
            },
            size: {
                width: 150,
                height: 45
            },
            originalGuid: guid,
            name: '',
            showName: true,
            selected: false,
            decimals: 2,
            type: null,
            color: null,
            symbol: null,
            isSymbolVisible: false,
            isLineVisible: false,
            isBarsVisible: false,
            relativePath: '',
            valueType: 'average',
            serieState: 0,
            showIcon: true
        };
        this.series.push(newSeries);
        this.updateRows(true);
    }

    private update(oldValue: string, oldGuid: string, newGuid: string) {
        const old = _.find(this.series, s => s.guid === oldGuid && s.value === oldValue);
        const svm = toDictionary(this.viewModels)[newGuid];
        const newValueType = this._seriesSelectorService.getAvailableValueType(svm, this.series, old);
        const isIdentifier = svm.objectType === ObjectType.Identifier;

        old.guid = svm.guid;
        old.value = newValueType;
        old.isBoundToGroup = isIdentifier && old.isBoundToGroup;
        old.originalGuid = svm.guid;
        old.type = svm.objectType as any;

        this.updateRows(false);
    }
}

export const wtFlowChartSeries: ComponentDefinition = {
    componentId: 'wtFlowChartSeries',
    controller: FlowChartSeriesController,
    template: require('./flowChartSeries.component.html'),
    controllerAs: 'vm',
    bindings: {
        isBindable: '=',
        series: '=',
        viewModels: '<'
    }
};